import { Aide } from '@/components/ui/aide';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, apiErrorMessage, clientsAPI, corridorsAPI } from '../lib/api';
import { estTarifFret, freightRulesAPI } from '../lib/economicsApi';
import { fetchAllPages } from '../lib/fetchAllPages';
import { formatMontant } from '../lib/formats';
import { libelle } from '../lib/libelles';
import { useAuth } from '../context/AuthContext';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageHeader } from '../components/ui/page-header';
import { EmptyState } from '../components/ui/empty-state';
import { Tag } from 'lucide-react';

const emptyRule = { name: '', price_per_liter: '', client_type: '', currency: 'XOF' };
const emptyFreightRule = { name: '', price_per_liter: '', corridor_id: '', destination: '', client_id: '' };

export default function PricingRulesPage() {
  const { canSeeMoney } = useAuth();
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(emptyRule);
  const [loading, setLoading] = useState(false);
  const [freightForm, setFreightForm] = useState(emptyFreightRule);
  const [freightSaving, setFreightSaving] = useState(false);
  const [corridors, setCorridors] = useState([]);
  const [clients, setClients] = useState([]);
  const [removalAsked, setRemovalAsked] = useState(null);

  const loadRules = async () => {
    const response = await api.get('/pricing-rules');
    const rows = Array.isArray(response.data) ? response.data : response.data?.data || [];
    setRules(rows);
  };

  useEffect(() => {
    loadRules().catch((error) => toast.error(apiErrorMessage(error, 'Impossible de charger les tarifs')));
  }, []);

  // Corridors et clients: tous, pas seulement la première page (menus de recherche).
  useEffect(() => {
    let cancelled = false;
    fetchAllPages((params) => corridorsAPI.getAll(params))
      .then(({ items }) => !cancelled && setCorridors(items || []))
      .catch(() => {});
    fetchAllPages((params) => clientsAPI.getAllPaged(params))
      .then(({ items }) => !cancelled && setClients(items || []))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const fuelRules = rules.filter((rule) => !estTarifFret(rule));
  const freightRules = rules.filter(estTarifFret);
  const clientName = (id) => clients.find((client) => client.id === id)?.name || 'Un client précis';
  const corridorLabel = (id) => {
    const corridor = corridors.find((item) => item.id === id);
    return corridor ? corridor.name || corridor.destination_label || 'Corridor' : 'Corridor';
  };
  const corridorOptions = corridors.map((corridor) => ({
    value: corridor.id,
    label: corridor.name || corridor.destination_label || 'Corridor sans nom',
    description: corridor.destination_label && corridor.destination_label !== corridor.name ? corridor.destination_label : undefined,
  }));
  const clientOptions = [
    { value: '', label: 'Tous les clients' },
    ...clients.map((client) => ({ value: client.id, label: client.name || 'Client sans nom' })),
  ];

  const submitFreight = async (event) => {
    event.preventDefault();
    if (!(Number(freightForm.price_per_liter) > 0)) {
      toast.error('Indiquez le tarif de fret par litre.');
      return;
    }
    if (!freightForm.corridor_id && !freightForm.destination.trim()) {
      toast.error('Choisissez un corridor ou indiquez une destination.');
      return;
    }
    setFreightSaving(true);
    try {
      await freightRulesAPI.create({
        name: freightForm.name.trim() || null,
        pricePerLiter: freightForm.price_per_liter,
        corridorId: freightForm.corridor_id,
        destination: freightForm.destination,
        clientId: freightForm.client_id,
      });
      setFreightForm(emptyFreightRule);
      await loadRules();
      toast.success('Tarif de fret enregistré');
    } catch (error) {
      toast.error(apiErrorMessage(error, "Impossible d'enregistrer le tarif de fret."));
    } finally {
      setFreightSaving(false);
    }
  };

  const toggleFreightRule = async (rule) => {
    try {
      await freightRulesAPI.setActive(rule.id, !rule.is_active);
      await loadRules();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Impossible de modifier ce tarif de fret.'));
    }
  };

  const removeFreightRule = async (rule) => {
    if (removalAsked !== rule.id) {
      setRemovalAsked(rule.id);
      return;
    }
    try {
      await freightRulesAPI.remove(rule.id);
      setRemovalAsked(null);
      await loadRules();
      toast.success('Tarif de fret supprimé');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Impossible de supprimer ce tarif de fret.'));
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await api.post('/pricing-rules', {
        ...form,
        price_per_liter: Number(form.price_per_liter || 0),
      });
      setForm(emptyRule);
      await loadRules();
      toast.success('Règle tarifaire enregistrée');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Impossible d’enregistrer la règle tarifaire.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Commercial"
        title="Tarifs"
        description="Prix du litre de carburant par client ou par type de client, puis tarifs de fret."
      />

      <form onSubmit={submit} className="grid gap-3 md:grid-cols-5">
        <Input placeholder="Nom de la règle" aria-label="Nom de la règle" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Input placeholder="Type de client (facultatif)" aria-label="Type de client" value={form.client_type} onChange={(e) => setForm({ ...form, client_type: e.target.value })} />
        <Input placeholder="Prix du litre (FCFA)" aria-label="Prix du litre" type="number" value={form.price_per_liter} onChange={(e) => setForm({ ...form, price_per_liter: e.target.value })} />
        <p className="flex items-center text-sm text-muted-foreground">Montants en FCFA</p>
        <Button type="submit" disabled={loading}>Enregistrer</Button>
      </form>

      {/* overflow-hidden clipped the table on narrow screens instead of
          letting it scroll, so the last columns were simply unreachable. */}
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="table-scroll">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-muted-foreground">
            <tr><th className="p-3">Nom</th><th>Client</th><th>Prix du litre</th><th>Statut</th></tr>
          </thead>
          <tbody>
            {/* Le formulaire de création est juste au-dessus, toujours
                visible: l'état vide n'a donc pas de bouton à offrir, il
                explique à quoi sert une règle et ce qui se passe sans. */}
            {fuelRules.length === 0 && (
              <tr>
                <td colSpan={4} className="p-0">
                  <EmptyState
                    icon={Tag}
                    title="Aucune règle tarifaire"
                    description="Une règle fixe le prix du litre appliqué à un client ou à un type de client. Sans règle, chaque commande doit être chiffrée à la main et rien ne garantit qu’un même client paie le même prix d’une fois sur l’autre."
                    className="border-0"
                  />
                </td>
              </tr>
            )}
            {fuelRules.map((rule) => (
              <tr key={rule.id} className="border-t border-border/60">
                <td className="p-3 font-medium">{rule.name || 'Règle sans nom'}</td>
                <td>{rule.client_type ? libelle('clientType', rule.client_type) : (rule.client_id ? clientName(rule.client_id) : 'Tous')}</td>
                <td className="tabular-nums">{formatMontant(rule.price_per_liter || 0, rule.currency || 'XOF', Number.isInteger(Number(rule.price_per_liter || 0)) ? 0 : 2)} le litre</td>
                <td><Badge variant={rule.is_active ? 'default' : 'secondary'}>{rule.is_active ? 'En vigueur' : 'Désactivée'}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>

      {canSeeMoney && (
        <section className="space-y-3" data-testid="tarifs-fret">
          <div>
            <div className="flex items-center gap-1">
              <h2 className="text-lg font-semibold">Tarifs de fret par litre</h2>
              <Aide titre="Tarifs de fret par litre">
                Ce que vous facturez pour transporter un litre sur un corridor ou vers une destination. Le tarif
                est proposé automatiquement sur chaque trajet; le plus précis l'emporte: d'abord le client, puis le
                corridor, puis la destination. Ce n'est jamais le prix du carburant.
              </Aide>
            </div>
          </div>

          <form onSubmit={submitFreight} className="grid gap-3 md:grid-cols-6">
            <Input
              className="md:col-span-2"
              placeholder="Nom (facultatif)"
              aria-label="Nom du tarif de fret"
              value={freightForm.name}
              onChange={(e) => setFreightForm({ ...freightForm, name: e.target.value })}
            />
            <div className="md:col-span-2">
              <SearchableSelect
                value={freightForm.corridor_id}
                onValueChange={(value) => setFreightForm({ ...freightForm, corridor_id: value })}
                options={[{ value: '', label: 'Aucun corridor' }, ...corridorOptions]}
                placeholder="Corridor"
                searchPlaceholder="Rechercher un corridor…"
                emptyMessage="Aucun corridor trouvé"
                aria-label="Corridor"
              />
            </div>
            <Input
              className="md:col-span-2"
              placeholder="Destination (si pas de corridor)"
              aria-label="Destination"
              value={freightForm.destination}
              onChange={(e) => setFreightForm({ ...freightForm, destination: e.target.value })}
            />
            <div className="md:col-span-2">
              <SearchableSelect
                value={freightForm.client_id}
                onValueChange={(value) => setFreightForm({ ...freightForm, client_id: value })}
                options={clientOptions}
                placeholder="Tous les clients"
                searchPlaceholder="Rechercher un client…"
                emptyMessage="Aucun client trouvé"
                aria-label="Client"
              />
            </div>
            <Input
              className="md:col-span-2"
              type="number"
              min="0"
              step="any"
              placeholder="Tarif par litre (FCFA)"
              aria-label="Tarif de fret par litre"
              value={freightForm.price_per_liter}
              onChange={(e) => setFreightForm({ ...freightForm, price_per_liter: e.target.value })}
            />
            <Button type="submit" className="md:col-span-2" disabled={freightSaving}>
              Enregistrer le tarif de fret
            </Button>
          </form>

          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="table-scroll">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-muted-foreground">
                  <tr>
                    <th className="p-3">Nom</th>
                    <th>Corridor ou destination</th>
                    <th>Client</th>
                    <th>Tarif par litre</th>
                    <th>Statut</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {freightRules.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-0">
                        <EmptyState
                          icon={Tag}
                          title="Aucun tarif de fret"
                          description="Sans tarif de fret, chaque trajet doit être chiffré à la main. Ajoutez le tarif par litre d'un corridor ou d'une destination pour qu'il soit proposé automatiquement."
                          className="border-0"
                        />
                      </td>
                    </tr>
                  )}
                  {freightRules.map((rule) => {
                    const conditions = rule.conditions_json || {};
                    return (
                      <tr key={rule.id} className="border-t border-border/60">
                        <td className="p-3 font-medium">{rule.name || 'Tarif sans nom'}</td>
                        <td>
                          {[
                            conditions.corridor_id ? corridorLabel(conditions.corridor_id) : null,
                            conditions.destination || null,
                          ]
                            .filter(Boolean)
                            .join(' · ') || 'Tous les trajets'}
                        </td>
                        <td>{rule.client_id ? clientName(rule.client_id) : 'Tous'}</td>
                        <td className="tabular-nums">{formatMontant(rule.price_per_liter || 0, rule.currency || 'XOF', Number.isInteger(Number(rule.price_per_liter || 0)) ? 0 : 2)} le litre</td>
                        <td>
                          <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                            {rule.is_active ? 'Actif' : 'Inactif'}
                          </Badge>
                        </td>
                        <td className="space-x-1 whitespace-nowrap p-2 text-right">
                          <Button type="button" variant="ghost" size="sm" onClick={() => toggleFreightRule(rule)}>
                            {rule.is_active ? 'Désactiver' : 'Réactiver'}
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeFreightRule(rule)}>
                            {removalAsked === rule.id ? 'Confirmer la suppression' : 'Supprimer'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
